import Immutable from 'immutable';

import Encode from 'sharedreact/utils/Encode';
import FileSupport from 'sharedreact/utils/FileSupport';

import type FolderEntry from 'sharedreact/data/FolderEntry';

const IS_CASE_INSENSITIVE = true; // AE server only runs on Windows for now

type URLComponents = {
  scheme: string,
  host: string,
  folders: Immutable.List<string>
};

// split in components
function _getComponents (urlString: string, normalize: boolean): URLComponents {
  let normalizedUrlString: string = urlString;
  if (normalize) {
    normalizedUrlString = FileSupport.normalizeUrlString(urlString);
  }

  const schemeIndex: number = normalizedUrlString.indexOf('://');
  const scheme: string = (schemeIndex !== -1 ? normalizedUrlString.substring(0, schemeIndex) : '');

  let host: string = '';
  let components: string[] = [];
  if (schemeIndex !== -1) {
    const pathWithoutScheme: string = normalizedUrlString.substring(schemeIndex + 3);

    if (scheme === 'eskocloud') {
      components = pathWithoutScheme.split('/');
    } else {
      const firstSeperator: number = pathWithoutScheme.indexOf('/');
      if (firstSeperator !== -1) {
        host = pathWithoutScheme.substring(0, firstSeperator).toLowerCase();
        if (firstSeperator !== pathWithoutScheme.length - 1) {
          const pathWithoutHost: string = pathWithoutScheme.substring(firstSeperator + 1);

          components = pathWithoutHost.split('/');
        }
      } else {
        host = pathWithoutScheme.toLowerCase();
      }
    }
  } else if (normalizedUrlString.length > 0) {
    components = normalizedUrlString.split('/');
  }

  return {
    scheme: scheme,
    host: host,
    folders: Immutable.List(components)
  };
}

function _componentsToUrlParts (components: URLComponents): {
  folder: string,
  filename: string
} {
  let folder: string = '';
  let filename: string = '';
  if (components.scheme !== '') {
    folder = components.scheme + '://';
  }

  if (components.folders.size > 0 && components.folders.last() === '') {
    components.folders = components.folders.pop();
  }

  if (components.folders.size > 1) {
    try {
      filename = decodeURIComponent(components.folders.last());
    } catch (e) {
      filename = components.folders.last();
    }
    if (components.host !== '') {
      folder += components.folders.pop().unshift(components.host).join('/');
    } else {
      folder += components.folders.pop().join('/');
    }
  } else if (components.folders.size === 1) {
    if (components.host !== '') {
      folder += components.folders.unshift(components.host).join('/');
    } else {
      folder += components.folders.first();
    }
  } else if (components.host !== '') {
    folder += components.host;
  }

  return {
    folder: folder,
    filename: filename
  };
}

function _normalize (json?: any): any {
  if (json != null && json.folder != null) {
    let normalizedJson: any = Object.assign({}, json);
    normalizedJson.folder = FileSupport.normalizeUrlString(json.folder);
    return normalizedJson;
  } else {
    return json;
  }
}

function areHostsEqual (host1: string, host2: string) {
  return host1.localeCompare(host2, 'en', {sensitivity: 'accent'}) === 0;
}

function areSharesEqual (share1: string, share2: string) {
  return share1.localeCompare(share2, 'en', {sensitivity: 'accent'}) === 0;
}

function areFoldersEqual (folder1: string, folder2: string) {
  if (IS_CASE_INSENSITIVE) {
    return folder1.localeCompare(folder2, 'en', {sensitivity: 'accent'}) === 0;
  }  else {
    return folder1 === folder2;
  }
}

const Content: {
  folder: string,
  filename: string
} = {
  folder: '',
  filename: ''
};

class Url extends Immutable.Record(Content) {
  constructor (json?: any) {
    super(_normalize(json));
  }

  static createFromString (path: string): Url {
    const components: URLComponents = _getComponents(path, true);

    return new Url(_componentsToUrlParts(components));
  }

  static fromJS (json: {}): Url {
    return new Url(Immutable.Map(json));
  }

  toString (): string {
    if (this.folder !== '') {
      if (this.filename !== '') {
        const urlStr = this.folder + '/' + FileSupport.strictEncodeURIComponent(this.filename);
        return urlStr;
      } else {
        return this.folder;
      }
    } else {
      return FileSupport.strictEncodeURIComponent(this.filename);
    }
  }

  isEmpty (): boolean {
    return (this.folder === '' && this.filename === '');
  }

  get fileUrl (): string | null {
    if (this.folder !== '' && this.filename !== '') {
      const fileUrl = this.folder + '/' + this.filename.split('/').map((component) => FileSupport.strictEncodeURIComponent(component)).join('/');
      return Encode.normalizeHexChars(fileUrl);
    } else if (this.folder !== '') {
      return Encode.normalizeHexChars(this.folder);
    }
    return null;
  }

  goDown (filename: string): Url {
    return new Url({folder: this.toString(), filename: filename});
  }

  goUp (): Url {
    return Url.createFromString(this.folder);
  }

  // split path in [<containername[@<host>]>, <folder>...]
  getDisplayComponents (containers?: Immutable.List<FolderEntry> | null): Immutable.List<string> {
    const folderComponents: URLComponents = _getComponents(this.folder, false);

    let displayComponents: Immutable.List<string> = folderComponents.folders.map((folder) => {
      try {
        return decodeURIComponent(folder);
      } catch (e) {
        return folder;
      }
    });

    if (folderComponents.host !== '') {
      if (!displayComponents.isEmpty()) {
        // correct drive letter if needed
        let firstFolder: string = displayComponents.first();

        if (firstFolder.match(/^([a-zA-Z])\$$/)) {
          firstFolder = firstFolder.replace(/^([a-zA-Z])\$$/, '$1:');
          displayComponents = displayComponents.set(0, firstFolder);
        }

        // replace container name derived from url by actual container name
        let replacedContainerName: boolean = false;

        const containerUrl: string = folderComponents.scheme + '://' + folderComponents.host + '/' + folderComponents.folders.first() + '/';

        if (containers != null) {
          const containerForUrl: FolderEntry | undefined = containers.find((container: FolderEntry) => container.url === containerUrl);
          if (containerForUrl != null) {
            displayComponents = displayComponents.set(0, containerForUrl.name);
            replacedContainerName = true;
          }
        }

        if (!replacedContainerName) {
          const host: string = decodeURIComponent(folderComponents.host);
          displayComponents = displayComponents.set(0, displayComponents.first() + '@' + host);
        }
      }
    }

    return this.filename !== '' ? displayComponents.push(this.filename) : displayComponents;
  }

  isDescendantOf (folder: string | Url): boolean {
    const folderComponents: URLComponents = (folder instanceof Url ? _getComponents(folder.toString(), false) : _getComponents(folder, true));
    const thisComponents: URLComponents = _getComponents(this.toString(), false);

    if (folderComponents.scheme === thisComponents.scheme &&
      areHostsEqual(folderComponents.host, thisComponents.host)) {
      if (folderComponents.folders.size > 0 && folderComponents.folders.size <= thisComponents.folders.size) {
        if (!areSharesEqual(folderComponents.folders.get(0)!, thisComponents.folders.get(0)!)) {
          return false;
        }
        
        for (let folderComponentIndex = 1; folderComponentIndex < folderComponents.folders.size; folderComponentIndex++) {
          const folderComponent: string = folderComponents.folders.get(folderComponentIndex)!;
          const thisComponent: string = thisComponents.folders.get(folderComponentIndex)!;

          if (!areFoldersEqual(folderComponent, thisComponent)) {
            return false;
          }
        }
        return true;
      }
    }
    return false;
  }

  getPathRelativeTo (folder: string | Url): Url | null {
    const folderComponents: URLComponents = (folder instanceof Url ? _getComponents(folder.toString(), false) : _getComponents(folder, true));
    const thisComponents: URLComponents = _getComponents(this.toString(), false);

    if (folderComponents.scheme === thisComponents.scheme &&
      areHostsEqual(folderComponents.host, thisComponents.host)
    ) {
      if (folderComponents.folders.size > 0 && folderComponents.folders.size <= thisComponents.folders.size) {
        if (!areSharesEqual(folderComponents.folders.get(0)!, thisComponents.folders.get(0)!)) {
          return null;
        }
        
        for (let folderComponentIndex = 1; folderComponentIndex < folderComponents.folders.size; folderComponentIndex++) {
          const folderComponent: string = folderComponents.folders.get(folderComponentIndex)!;
          const thisComponent: string = thisComponents.folders.get(folderComponentIndex)!;

          if (!areFoldersEqual(folderComponent, thisComponent)) {
            return null;
          }
        }

        const relativeComponentsFolders: Immutable.List<string> = thisComponents.folders.slice(folderComponents.folders.size);
        const relativeComponents: URLComponents = {
          scheme: '',
          host: '',
          folders: relativeComponentsFolders
        };

        return new Url(_componentsToUrlParts(relativeComponents));
      }
    }

    return null;
  }

  isEqual (url: Url): boolean {
    const thisComponents = _getComponents(this.toString(), false);
    const inputUrlComponents = _getComponents(url.toString(), false);

    if (thisComponents.folders.size !== inputUrlComponents.folders.size) {
      return false;
    }

    if (thisComponents.scheme !== inputUrlComponents.scheme) {
      return false;
    }

    if (!areHostsEqual(thisComponents.host, inputUrlComponents.host)) {
      return false;
    }

    const thisShare = thisComponents.folders.get(0);
    const inputUrlShare = inputUrlComponents.folders.get(0);

    if (thisShare == null && inputUrlShare == null) {
      return true;
    } else if (thisShare == null || inputUrlShare == null) {
      return false;
    }
      
    if (!areSharesEqual(thisShare, inputUrlShare)) {
      return false;
    }

    if (thisComponents.folders.size === inputUrlComponents.folders.size) {
      for (let folderIndex = 1 ; folderIndex < thisComponents.folders.size; folderIndex++) {
          const thisComponent: string = thisComponents.folders.get(folderIndex)!;
          const inputUrlComponent: string = inputUrlComponents.folders.get(folderIndex)!;

          if (!areFoldersEqual(thisComponent, inputUrlComponent)) {
            return false;
          }
      }

      return true;
    }

    return false;
  }
}

function isValidAbsoluteFilePath (path: string): boolean {
  const isInvalidURLComponent = (folder: string) => {
    try {
      decodeURIComponent(folder);
      return false;
    } catch (e) {
    }
    return true;
  };

  const folderComponents: URLComponents = _getComponents(path, true);

  return (
    folderComponents.scheme === 'file' &&
    folderComponents.host !== '' &&
    !isInvalidURLComponent(folderComponents.host) &&
    (folderComponents.folders.size > 0) &&
    folderComponents.folders.findIndex(isInvalidURLComponent) === -1
  );
}

function areUrlStringsEqual (url1: string, url2: string) {
  return Url.createFromString(url1).isEqual(Url.createFromString(url2));
}

function areUrlsEqual (url1: Url, url2: Url) {
  return url1.isEqual(url2);
}

export {
  isValidAbsoluteFilePath,
  areUrlStringsEqual,
  areUrlsEqual
};

export default Url;
